import { appendStreamChunkToBuffer } from "@/llm-generation/client-runtime"

import { getOrganizationBearerHeaders } from "@/utils/auth/organization-token"

import type {
  ReportRevisionEvent,
  ReportRevisionStream,
} from "./reportRevisionTypes"

function readEvent(
  kind: string,
  data: unknown,
  requestId: string,
  notebookId: string,
): ReportRevisionEvent {
  if (
    !data ||
    typeof data !== "object" ||
    !("request_id" in data) ||
    data.request_id !== requestId
  ) {
    throw new Error("Sofia returned an event for a different request.")
  }
  const value = data as Record<string, unknown>
  if (kind.startsWith("section_") && typeof value.section_id !== "string") {
    throw new Error("Sofia returned an invalid section update.")
  }
  if (
    kind === "request_started" &&
    (!Array.isArray(value.section_ids) ||
      !value.section_ids.every((id) => typeof id === "string"))
  ) {
    throw new Error("Sofia returned invalid revision targets.")
  }
  if (kind === "section_delta" && typeof value.body !== "string") {
    throw new Error("Sofia returned invalid draft text.")
  }
  if (
    (kind === "error" || kind === "section_error") &&
    typeof value.error !== "string"
  ) {
    throw new Error("Sofia returned an invalid revision error.")
  }
  if (kind === "section_final" || kind === "request_final") {
    const notebook = value.notebook as
      | { id?: unknown; revision?: unknown; sections?: unknown }
      | undefined
    if (
      notebook?.id !== notebookId ||
      !Number.isInteger(notebook.revision) ||
      !Array.isArray(notebook.sections)
    ) {
      throw new Error("Sofia returned an invalid saved notebook.")
    }
  }
  if (kind === "request_final") {
    const turn = value.turn as { id?: unknown; status?: unknown } | undefined
    if (
      turn?.id !== requestId ||
      !["applied", "unchanged", "partial", "failed"].includes(
        String(turn.status),
      )
    ) {
      throw new Error("Sofia returned an invalid conversation outcome.")
    }
  }
  if (
    ![
      "request_started",
      "section_started",
      "section_delta",
      "section_final",
      "section_error",
      "request_final",
      "error",
    ].includes(kind)
  ) {
    throw new Error("Sofia returned an unknown revision event.")
  }
  return { ...value, type: kind } as ReportRevisionEvent
}

export function createReportRevisionStream(
  organizationSlug: string,
  workspaceId: string,
): ReportRevisionStream {
  return async (notebookId, payload, onEvent, signal) => {
    const url = new URL(
      `/api/sofia/notebook-reports/notebooks/${encodeURIComponent(notebookId)}/revise`,
      window.location.origin,
    )
    url.searchParams.set("organization", organizationSlug)
    url.searchParams.set("workspace", workspaceId)
    const headers = await getOrganizationBearerHeaders(organizationSlug)
    signal.throwIfAborted()
    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        ...headers,
        Accept: "text/event-stream",
        "Content-Type": "application/json",
      },
      cache: "no-store",
      signal,
    })
    if (!response.ok) {
      const failure = (await response.json().catch(() => null)) as {
        error?: unknown
      } | null
      throw new Error(
        typeof failure?.error === "string"
          ? failure.error
          : "Sofia could not start this revision.",
      )
    }
    if (
      !response.body ||
      !response.headers.get("content-type")?.includes("text/event-stream")
    ) {
      throw new Error("Sofia did not return a live revision stream.")
    }
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ""
    let finished = false
    const abort = () => {
      void reader.cancel().catch(() => undefined)
    }
    signal.addEventListener("abort", abort, { once: true })
    try {
      while (!finished) {
        const { value, done } = await reader.read()
        signal.throwIfAborted()
        if (done) {
          break
        }
        const normalized = (
          buffer + decoder.decode(value, { stream: true })
        ).replace(/\r\n/g, "\n")
        if (normalized.length > 2_000_000) {
          throw new Error("The revision stream exceeded its size limit.")
        }
        const next = appendStreamChunkToBuffer({
          buffer: "",
          chunk: normalized,
        })
        buffer = next.buffer
        if (buffer.length > 2_000_000) {
          throw new Error("The revision stream exceeded its size limit.")
        }
        for (const frame of next.events) {
          const event = readEvent(
            frame.event,
            frame.data,
            payload.request_id,
            notebookId,
          )
          onEvent(event)
          if (event.type === "request_final") {
            finished = true
          }
          if (event.type === "error") {
            throw new Error(event.error)
          }
          if (finished) {
            break
          }
        }
      }
      if (!finished) {
        throw new Error(
          "The live connection ended. Saved sections are kept; reconnect to check the outcome.",
        )
      }
    } finally {
      signal.removeEventListener("abort", abort)
      await reader.cancel().catch(() => undefined)
      reader.releaseLock()
    }
  }
}
